import { OwnerType, OwnerTypes } from "@/domain/_shared/lib/owner-types";
import { Assignee } from "@/domain/user/models/assignee-model";
import { CreateAssigneeInput, CreateAssigneeMutation } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";

import { createAssigneeMutationDocument } from "./assignee.gql";
import { EVENT_ASSIGNEE_CREATED, trackAssigneeEvent } from "./tracking";
export type CreatedAssignee = CreateAssigneeMutation["createAssignee"];

export interface UseCreateAssigneeProps {
  ownerType: OwnerType;
  onCompleted?: (createdAssignee: CreatedAssignee) => void;
  onError?: (e: Error) => void;
}

interface EntityWithAssigneeList {
  id: string;
  assigneeList?: Assignee[] | null;
}

const toAssignee = (createdAssignee: CreatedAssignee): Assignee => ({
  id: createdAssignee.id,
  userId: createdAssignee.userId ?? undefined,
  workspaceId: createdAssignee.workspaceId,
  ownerId: createdAssignee.ownerId,
  ownerType: createdAssignee.ownerType,
  user: createdAssignee.user
    ? {
        id: createdAssignee.user.id,
        email: createdAssignee.user.email,
        username: createdAssignee.user.username ?? null,
        fullname: createdAssignee.user.fullname ?? null,
        profilePicture: createdAssignee.user.profilePicture ?? null,
      }
    : undefined,
});

const appendAssigneeToEntity = <TEntity extends EntityWithAssigneeList>(
  entity: TEntity,
  assignee: Assignee,
): TEntity => {
  const alreadyAssigned = entity.assigneeList?.some((existing) => existing.id === assignee.id);
  if (alreadyAssigned) {
    return entity;
  }

  return {
    ...entity,
    assigneeList: [...(entity.assigneeList ?? []), assignee],
  };
};

const patchTaskCaches = (queryClient: QueryClient, assignee: Assignee): void => {
  queryClient.setQueriesData<EntityWithAssigneeList[]>({ queryKey: queryKeys.tasks.lists() }, (taskList) => {
    if (!taskList) {
      return taskList;
    }

    let hasChanged = false;
    const nextTaskList = taskList.map((task) => {
      if (task.id !== assignee.ownerId) {
        return task;
      }
      const nextTask = appendAssigneeToEntity(task, assignee);
      if (nextTask !== task) {
        hasChanged = true;
      }
      return nextTask;
    });

    return hasChanged ? nextTaskList : taskList;
  });

  queryClient.setQueryData<EntityWithAssigneeList>(queryKeys.tasks.detail(assignee.ownerId), (task) =>
    task ? appendAssigneeToEntity(task, assignee) : task,
  );
};

export const useCreateAssignee = ({ onCompleted, onError }: UseCreateAssigneeProps) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: CreateAssigneeInput) => {
      const client = createOperationClient("createAssignee");
      const data = await client.request(createAssigneeMutationDocument, { input });
      return data.createAssignee;
    },
    onSuccess: (newAssignee) => {
      // Invalidate assignee queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.assignees.detail(newAssignee.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.assignees.byOwner(newAssignee.ownerId),
      });

      // Invalidate related entity queries
      if (newAssignee.ownerType === OwnerTypes.CLIMATE_ACTION) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.climateActions.detail(newAssignee.ownerId),
        });
        // Also invalidate climate action lists to update datatable section headers
        queryClient.invalidateQueries({
          queryKey: queryKeys.climateActions.lists(),
        });
      }

      if (newAssignee.ownerType === OwnerTypes.PROJECT) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.detail(newAssignee.ownerId),
        });
        // Also invalidate project lists to update datatable section headers
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.lists(),
        });
      }

      if (newAssignee.ownerType === OwnerTypes.TASK) {
        patchTaskCaches(queryClient, toAssignee(newAssignee));
      }

      trackAssigneeEvent(EVENT_ASSIGNEE_CREATED, newAssignee);

      if (onCompleted) {
        onCompleted(newAssignee);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    createAssignee: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
